import assertNever from "assert-never";
import { Duration, Instant } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import { API } from "../API";
import { isLoading } from "../Loading";
import * as messages from "../messages";
import { STRINGS } from "../strings";
import { SendingChatMessage } from "../system/AppState/ChatState";
import { globalize, translate } from "../translate";
import ChatMessagePayload from "./ChatMessagePayload";
import { ChatMessageAuthor, ChatMessageSender } from "./ChatRoomScreen";

interface ChatMessageViewProps {
  /**
   * Either this or `sendingMessage` must be set
   */
  message: messages.ChatRoomMessage | null;

  /**
   * Either this or `message` must be set
   */
  sendingMessage: SendingChatMessage | null;

  seenStatus: boolean;

  author: ChatMessageAuthor;
  avatarPopoverOpen: boolean;
  getSignedPhotoUrl: API["getSignedPhotoUrl"];
  onOpenAvatarPopover: (requestId: string) => void;
  onCloseAvatarPopover: (requestId: string) => void;
}

const ChatMessageView = (props: ChatMessageViewProps) => {
  const avatarRef = React.useRef<HTMLDivElement>(null);

  const requestId = (() => {
    if (props.message !== null) {
      return props.message.requestId;
    }

    if (props.sendingMessage !== null) {
      return props.sendingMessage.requestId;
    }

    throw new Error("The Impossible Happened: requestId");
  })();

  const chatMessagePayload = (() => {
    if (props.message !== null) {
      return props.message.payload;
    }

    if (props.sendingMessage !== null) {
      return props.sendingMessage.payload;
    }

    throw new Error("The Impossible Happened: chatMessagePayload");
  })();

  const chatBubbleFooter = (() => {
    if (props.message !== null) {
      const now = Instant.now();
      const messageCreatedAt = props.message.createdAt;
      const timestampStr = messageCreatedAt.isBefore(now.minus(Duration.ofHours(12)))
        ? globalize().formatInstant(messageCreatedAt, { skeleton: "yMMMEEEdjmmss" })
        : globalize().formatInstant(messageCreatedAt, { time: "medium" });

      return <>{timestampStr}</>;
    }

    if (props.sendingMessage !== null) {
      switch (props.sendingMessage.status.type) {
        case "Sending":
          return <>Sending...</>;
        case "Error":
          // TODO click to retry (and click (i) button to view error)
          return <>{translate(STRINGS.ERROR)}</>;
        default:
          return assertNever(props.sendingMessage.status);
      }
    }

    throw new Error("The Impossible Happened: chatBubbleFooter");
  })();

  const chatBubbleSender = (() => {
    if (props.message !== null) {
      return props.author.type === "AgencyMember"
        ? ChatMessageSender.Received
        : ChatMessageSender.Sent;
    }

    if (props.sendingMessage !== null) {
      return ChatMessageSender.Sending;
    }

    throw new Error("The Impossible Happened: chatBubbleSender");
  })();

  return (
    <div className="ChatRoomScreen__messageRow">
      {props.author.type === "AgencyMember"
        ? (() => {
            return (
              <div>
                <div ref={avatarRef} onClick={() => props.onOpenAvatarPopover(requestId)}>
                  {isLoading(props.author.agencyMember) ||
                  props.author.agencyMember.photoUrl === null ? (
                    <div className="ChatRoomScreen__senderPhoto ChatRoomScreen__senderPhoto_default" />
                  ) : (
                    <img
                      className="ChatRoomScreen__senderPhoto"
                      src={props.author.agencyMember.photoUrl}
                    />
                  )}
                </div>
                {avatarRef.current === null ? null : (
                  <Ons.Popover
                    getTarget={() => avatarRef.current as HTMLDivElement}
                    isOpen={props.avatarPopoverOpen}
                    isCancelable={true}
                    onCancel={() => props.onCloseAvatarPopover(requestId)}
                  >
                    <div className="ChatRoomScreen__avatarPopover">
                      {isLoading(props.author.agencyMember) || isLoading(props.author.agency) ? (
                        <Ons.ProgressCircular indeterminate={true} />
                      ) : (
                        <div>
                          {props.author.agencyMember.firstName} {props.author.agencyMember.lastName}
                          <div className="ChatRoomScreen__avatarPopover__body">
                            <div className="ChatRoomScreen__avatarPopover__body_left">
                              <div className="ChatRoomScreen__avatarPopover__agencyName">
                                {props.author.agency.name}
                              </div>
                              <div className="ChatRoomScreen__avatarPopover__jobTitle">
                                {props.author.agencyMember.jobTitle}
                              </div>
                            </div>
                            <img
                              className="ChatRoomScreen__avatarPopover__agencyLogo"
                              src={
                                props.author.agency.logoUrl === null
                                  ? "./assets/agency_logo.png"
                                  : props.author.agency.logoUrl
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </Ons.Popover>
                )}
              </div>
            );
          })()
        : null}
      <ChatMessagePayload
        payload={chatMessagePayload}
        seenStatus={props.seenStatus}
        sender={chatBubbleSender}
        footer={chatBubbleFooter}
        getSignedPhotoUrl={props.getSignedPhotoUrl}
      />
    </div>
  );
};

export default ChatMessageView;
